if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.requestVersion = 0;
        this.focusElement = this.input;

        this.addEventListener('reset', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('focus', this.onFocus.bind(this));

        this.input.addEventListener('input', () => {
          this.hideTypewriter();

          // Invalidate old requests immediately, before the debounce.
          this.cancelPendingSearch();
          this.resetResults();

          if (!this.getQuery()) return;

          this.setAttribute('loading', '');

          this.searchTimer = setTimeout(() => {
            this.onChange();
          }, 300);
        });

        if (this.getQuery()) this.hideTypewriter();
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.querySelector('.search__field .typed');
      }

      get resultsContainer() {
        return this.querySelector('[id^="PredictiveSearchResults-"]');
      }

      hideTypewriter() {
        const element = this.typewriter;
        if (!element) return;

        element.setAttribute('hidden', '');
        element.setAttribute('aria-hidden', 'true');
        element.style.setProperty('display', 'none', 'important');
      }

      cancelPendingSearch() {
        clearTimeout(this.searchTimer);
        this.requestVersion += 1;

        this.abortController?.abort();
        this.abortController = null;
        this.removeAttribute('loading');
      }

      resetResults() {
        this.removeAttribute('results');

        if (this.resultsContainer) {
          this.resultsContainer.replaceChildren();
        }
      }

      onFocus() {
        this.hideTypewriter();

        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');
        }

        if (!this.getQuery()) return;

        this.renderSection(this.buildUrl().toString());
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.cancelPendingSearch();
        this.input.value = '';
        this.resetResults();
        this.input.focus();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(
          `${theme.routes.shop_url}${theme.routes.predictive_search_url}`
        );

        const configuredLimit = parseInt(
          this.getAttribute('data-limit'),
          10
        );

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set(
          'resources[limit]',
          configuredLimit > 0 ? configuredLimit : 3
        );
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));

        return url;
      }

      onChange() {
        if (!this.getQuery()) {
          this.cancelPendingSearch();
          this.resetResults();
          return;
        }

        this.hideTypewriter();
        this.renderSection(this.buildUrl().toString());
      }

      async renderSection(url) {
        // Cancel old work even when the new result is cached.
        this.cancelPendingSearch();

        const query = this.getQuery();
        const version = this.requestVersion;

        this.resetResults();
        if (!query) return;

        const controller = new AbortController();
        this.abortController = controller;
        this.setAttribute('loading', '');

        const isCurrentSearch = () =>
          version === this.requestVersion &&
          query === this.getQuery() &&
          !controller.signal.aborted;

        try {
          let responseText;

          if (this.cachedMap.has(url)) {
            responseText = this.cachedMap.get(url);
          } else {
            const response = await fetch(url, {
              signal: controller.signal
            });

            if (!response.ok) {
              throw new Error(`Search request failed: ${response.status}`);
            }

            responseText = await response.text();
          }

          // An older query must never overwrite the current results.
          if (!isCurrentSearch()) return;

          if (!this.renderSearchResults(responseText)) {
            this.cachedMap.delete(url);
            throw new Error('Search results markup was not found.');
          }

          this.cachedMap.set(url, responseText);
          this.setAttribute('results', '');
        } catch (error) {
          if (error.name !== 'AbortError' && isCurrentSearch()) {
            this.resetResults();
            console.error('Predictive search:', error);
          }
        } finally {
          // Old requests must not clear a newer request's loading state.
          if (version === this.requestVersion) {
            this.removeAttribute('loading');
            this.abortController = null;
          }
        }
      }

      renderSearchResults(responseText) {
        const target = this.resultsContainer;
        if (!target) return false;

        const parsedDocument = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

        const source = parsedDocument.getElementById(target.id);
        if (!source) return false;

        // Update only this form's results.
        target.innerHTML = source.innerHTML;
        return true;
      }
    },
    { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
